import { keepPreviousData } from "@tanstack/react-query"
import { useDebounceValue } from "usehooks-ts"

import type { PackageOp } from "~/utils/allocationPackageOps"
import { api, type RouterOutputs } from "~/utils/api"
import { cn } from "~/utils/helpers"

type DraftImpact = Extract<
  RouterOutputs["allocation"]["getPackageDraftImpact"],
  { ok: true }
>["impact"]
type DraftImpactLine = DraftImpact["lines"][number]

function deltaClass(delta: number) {
  if (delta > 0) return "text-badge-green-fg-text"
  if (delta < 0) return "text-badge-orange-fg-text"
  return "text-fg-muted"
}

const signed = (delta: number) => `${delta >= 0 ? "+" : ""}${delta.toLocaleString()}`

function skuLabel(line: DraftImpactLine) {
  const name = line.variantName ?? line.productName ?? line.skuId
  return line.size ? `${name} · ${line.size}` : name
}

function ImpactLines({ impact }: { impact: DraftImpact }) {
  return (
    <div className="mt-2 flex max-h-40 flex-col gap-1 overflow-y-auto">
      {impact.lines.map((line) => (
        <div key={`${line.storeId}:${line.skuId}`} className="flex items-baseline gap-2">
          <span className="paragraph-xs min-w-0 flex-1 truncate text-fg-subtle">
            {line.storeName} · {skuLabel(line)}
          </span>
          <span className="paragraph-xs tabular-nums text-fg-muted line-through">
            {line.before.toLocaleString()}
          </span>
          <span className="paragraph-xs text-fg-muted">→</span>
          {line.rateOfSalePending ? (
            <span className="paragraph-xs text-fg-muted">after run</span>
          ) : (
            <span className="paragraph-xs min-w-8 text-right font-semibold tabular-nums">
              {line.after.toLocaleString()}
            </span>
          )}
        </div>
      ))}
      {impact.linesChanged > impact.lines.length && (
        <div className="paragraph-xs text-fg-muted">
          Showing the {impact.lines.length} largest of {impact.linesChanged} changes.
        </div>
      )}
    </div>
  )
}

const count = (n: number, singular: string) =>
  `${n.toLocaleString()} ${singular}${n === 1 ? "" : "s"}`

function ImpactBody({ impact }: { impact: DraftImpact }) {
  if (impact.linesChanged === 0) {
    return (
      <div className="paragraph-xs text-fg-muted">
        0 units. These changes move no suggested qty today.
      </div>
    )
  }
  const delta = impact.unitsAfter - impact.unitsBefore
  return (
    <>
      <div className="label-base tabular-nums">
        {impact.unitsAfter.toLocaleString()} u
        <span className={cn("paragraph-xs ml-1.5", deltaClass(delta))}>{signed(delta)}</span>
      </div>
      <div className="paragraph-xs text-fg-muted">
        was {impact.unitsBefore.toLocaleString()} u for these products
      </div>
      <div className="paragraph-xs mt-1.5 text-fg-muted">
        {count(impact.linesChanged, "line")} change in {count(impact.stores, "store")}.
      </div>
      {impact.rateOfSalePending > 0 && (
        <div className="paragraph-xs mt-1.5 text-fg-muted">
          {count(impact.rateOfSalePending, "SKU")} new to a store get their ROS on Publish, so the
          run after it sets their suggested qty.
        </div>
      )}
      <ImpactLines impact={impact} />
    </>
  )
}

/** The Details aside for the package draft: how publishing it moves suggested qty at the stores it reruns. */
export function PackageDraftImpact({ ops }: { ops: PackageOp[] }) {
  // Quick successive edits fire one engine run, not one each.
  const [settledOps] = useDebounceValue(ops, 600)
  const query = api.allocation.getPackageDraftImpact.useQuery(
    { ops: settledOps },
    {
      placeholderData: keepPreviousData,
      staleTime: 60_000,
      refetchInterval: (q) =>
        q.state.data && !q.state.data.ok && q.state.data.busy ? 2_000 : false,
    },
  )
  const body = () => {
    if (query.isLoading) {
      return (
        <div
          className="flex animate-pulse flex-col gap-1.5"
          role="status"
          aria-label="Estimating impact"
        >
          <div className="h-4 w-24 rounded bg-component-hover" />
          <div className="h-3 w-16 rounded bg-component-hover" />
        </div>
      )
    }
    if (query.isError || !query.data) {
      return (
        <div className="paragraph-xs text-fg-muted">
          Couldn&apos;t estimate the impact. Publishing still works.
        </div>
      )
    }
    if (!query.data.ok) return <div className="paragraph-xs text-fg-muted">{query.data.error}</div>
    return <ImpactBody impact={query.data.impact} />
  }
  return (
    <div
      className={cn(
        "w-80 shrink-0 border-l border-base px-3.5 py-2.5",
        query.isPlaceholderData && "opacity-60",
      )}
    >
      <div className="label-sm mb-1.5 text-fg-muted">Impact on suggested qty</div>
      {body()}
    </div>
  )
}
